import { Dispatch } from 'react';

import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';

import { generatePDF, getRespondentNameForSurveyPdf } from './survey.util';
import util from './util';
import {
  AssessmentAction,
  CreateAssessmentIntent,
} from '../constants/patients.constants';
import {
  PortalComponent,
  SetComponentVisibilityAction,
} from '../contexts/ComponentPortalContext';

// Function to record a new survey
export const onRecordNewSurvey = (
  surveyName: SurveyName,
  updateAssessmentState: Dispatch<AssessmentAction>,
): void => {
  updateAssessmentState({
    data: {
      createAssessmentIntent: CreateAssessmentIntent.BEGIN_NOW,
      selectedAssessment: {
        status: SurveyResponseStatus.NEW,
        surveyName,
      },
    },
    field: 'SET',
  });
};

// Function to send a email to start the survey
export const onSendNewSurvey = (
  surveyName: SurveyName,
  updateAssessmentState: Dispatch<AssessmentAction>,
): void => {
  updateAssessmentState({
    data: {
      createAssessmentIntent: CreateAssessmentIntent.SEND_INVITATION,
      selectedAssessment: {
        status: SurveyResponseStatus.NEW,
        surveyName,
      },
    },
    field: 'SET',
  });
};

// Function to download the completed survey results in pdf format
export const onDownloadSurveyPdf = (
  externalId: string,
  assessmentData: SurveyResponse[],
  patientPreferredName = '',
): void => {
  const assessment = (assessmentData ?? []).find(
    ({ responseExternalId }: SurveyResponse) =>
      responseExternalId === externalId,
  );
  if (assessment) {
    const {
      endDate,
      jsonResults,
      submittedUserDisplayFirstName,
      submittedUserDisplayLastName,
      submittedUserName,
      surveyDefinition,
      surveyName,
      toUserFirstName,
      toUserLastName,
    } = assessment;

    generatePDF({
      endDate,
      jsonResults: util.parseJson(jsonResults),
      patientName: getRespondentNameForSurveyPdf(
        surveyName,
        patientPreferredName,
        toUserFirstName,
        toUserLastName,
        submittedUserDisplayFirstName,
        submittedUserDisplayLastName,
        submittedUserName,
      ),
      surveyDefinition: util.parseJson(surveyDefinition),
      surveyName,
    });
  }
};

export const onDownloadAssessmentPdf = (
  assessment: SurveyResponse,
  patientPreferredName = '',
): void => {
  if (assessment) {
    const {
      endDate,
      jsonResults,
      submittedUserDisplayFirstName,
      submittedUserDisplayLastName,
      submittedUserName,
      surveyDefinition,
      surveyName,
      toUserFirstName,
      toUserLastName,
    } = assessment;

    generatePDF({
      endDate,
      jsonResults: util.parseJson(jsonResults),
      patientName: getRespondentNameForSurveyPdf(
        surveyName,
        patientPreferredName,
        toUserFirstName,
        toUserLastName,
        submittedUserDisplayFirstName,
        submittedUserDisplayLastName,
        submittedUserName,
      ),
      surveyDefinition: util.parseJson(surveyDefinition),
      surveyName,
    });
  }
};

// Function to continue a incomplete survey using external ID
// Open survey accordion and load current assessment data
export const onContinueSurvey = (
  externalId: string,
  assessmentData: SurveyResponse[],
  setComponentVisibility: SetComponentVisibilityAction,
): void => {
  const existing = (assessmentData ?? []).find(
    ({ responseExternalId }: SurveyResponse) =>
      responseExternalId === externalId,
  );
  if (existing) {
    setComponentVisibility({
      component: PortalComponent.SURVEY_ACCORDION,
      data: existing,
      isExpanded: true,
      isVisible: true,
    });
  }
};
export const onAssessmentResume = (
  assessment: SurveyResponse,
  setComponentVisibility: SetComponentVisibilityAction,
): void => {
  if (assessment) {
    setComponentVisibility({
      component: PortalComponent.SURVEY_ACCORDION,
      data: assessment,
      isExpanded: true,
      isVisible: true,
    });
  }
};
